import Vue from 'vue';
import i18next from 'i18next';
import numeral from 'numeral';
import Raven from 'raven-js';
import Debug from '~/utils/debug';
import { setInterfaceLanguage } from '~/store/actions';
import * as LocalStorage from '~/utils/local-storage';
import createInterpolation from './i18n-interpolation';
import { LOCALE_DEFAULT, LOCALE_FALLBACK, ENV_IS_DEVELOPMENT, LOCALE_DEVELOPMENT } from './constants';

const debug = Debug('i18n');

export const eventBus = new Vue();

const getLocales = context => context
  .keys()
  .map(file => {
    const [, localeCode] = file.match(/([a-z]+)\.ya?ml$/) || [];
    if (!localeCode) {
      return null;
    }

    return {
      code: localeCode,
      file: context(file),
    };
  })
  .filter(v => v)
  .reduce((res, curr) => Object.assign(res, { [curr.code]: { translation: curr.file } }), {});

let reportedMissing = [];

export const init = localeRequireContext => {
  i18next.init({
    fallbackLng: LOCALE_FALLBACK,
    lng: LOCALE_DEFAULT,
    resources: getLocales(localeRequireContext),
    interpolation: {
      format: createInterpolation(i18next.t.bind(i18next)),
      escape: val => val, // disable interpolation result escaping
    },
  });
};

export const reload = newLocaleRequireContext => {
  i18next.addResources(getLocales(newLocaleRequireContext));
  reportedMissing = [];
};

i18next.on('missingKey', (lngs, namespace, key) => {
  const lngArray = Array.isArray(lngs) ? lngs : [lngs];
  lngArray.forEach(lng => {
    const message = `lng: ${lng}, ${namespace}::${key}`;
    if (reportedMissing.indexOf(message) === -1) {
      reportedMissing.push(message);
      debug.warn('Missing key: %s', message);
    }
  });
});

const originalLoggerError = i18next.logger.logger.error.bind(i18next.logger.logger);
i18next.logger.logger.error = (args) => {
  args
    .filter(a => a instanceof Error)
    .forEach(e => {
      Raven.captureBreadcrumb({
        message: args[0],
        category: 'i18n',
        level: 'error',
        data: args,
      });
      Raven.captureException(e);
    });

  return originalLoggerError(args);
};

export default i18next;

export const updateTranslations = translations => {
  Object.keys(translations)
    .forEach(language => {
      i18next.addResources(language, 'translation', translations[language]);
      debug.info('updated language %s translations with %o', language, translations[language]);
    });
};

export const translateInLocale = (locale, key, params = {}) => {
  const t = i18next.getFixedT(locale);
  return t(key, params);
};

let availableLocales = ['en'];

export const getAvailableLocales = () => (ENV_IS_DEVELOPMENT ?
  [...availableLocales, LOCALE_DEVELOPMENT]
  : availableLocales);

export const setLocale = locale => {
  if (!getAvailableLocales().includes(locale)) {
    return;
  }

  debug.info('Changing locale to: %s', locale);
  i18next.changeLanguage(locale);
  LocalStorage.set('locale', locale);
  eventBus.$emit('language-changed', locale);
  numeral.locale(locale);
  setInterfaceLanguage(locale);
};

export const getLocale = () => i18next.language;


export const setAvailableLocales = locales => {
  availableLocales = locales;

  if (!new Set(availableLocales).has(i18next.language)) {
    setLocale(availableLocales[0]);
  }
};



// WEBPACK FOOTER //
// ./src/gotu-vue-common/src/utils/i18n.js